import { CalendarRange, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { useActiveYear } from '../hooks/useActiveYear';
import { useProgramYears } from '../hooks/useProgramYears';
import { useYearSelection } from '../hooks/useYearSelection';

/** 라디오 값 — "최근 연도 자동"은 숫자가 아니라 별도 값으로 둔다. */
const AUTO = 'auto';

interface YearSelectorProps {
  /** 사업 연도와 무관한 화면(route handle `yearIndependent`) — 선택을 비활성하고 "연도 무관"을 안내한다(#198). */
  independent?: boolean;
}

/**
 * 헤더 기준 연도 선택기(specs/032 FR-013·016) — 워크스페이스의 연도 기반 메뉴가 모두 이 값 하나를 따른다.
 * 첫 항목 "최근 연도 자동(YYYY)"은 미선택 상태의 표시이자 명시 선택 해제 동작이다. 선택지가 없으면 비활성.
 * 연도와 무관한 화면에서는 값을 바꿔도 결과가 같으므로 비활성 + 안내로 오해를 막는다(선택 값은 유지 — 연도 기반 화면으로 돌아가면 그대로).
 */
export function YearSelector({ independent = false }: YearSelectorProps) {
  const { data, isPending } = useProgramYears();
  const { displayYear, isExplicit } = useActiveYear();
  const select = useYearSelection();
  const years = data?.years ?? [];
  const empty = !isPending && years.length === 0;

  if (independent) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* disabled 대신 aria-disabled — 포커스·호버가 살아 있어야 키보드·마우스 모두 안내 툴팁을 볼 수 있다. */}
          <Button
            variant="outline"
            size="sm"
            className="cursor-default gap-1.5 text-muted-foreground hover:bg-transparent hover:text-muted-foreground"
            aria-disabled="true"
            aria-label="기준 연도 — 이 화면은 사업 연도와 무관합니다"
            onClick={(e) => e.preventDefault()}
          >
            <CalendarRange aria-hidden="true" />
            <span>연도 무관</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          이 화면은 사업 연도와 무관합니다. 연도 기준은 공고 찾기·대시보드·캘린더 등에 적용됩니다.
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={isPending || empty}
          aria-label={
            displayYear ? `기준 연도 ${displayYear}년${isExplicit ? '' : ' (자동)'}` : '기준 연도'
          }
        >
          <CalendarRange aria-hidden="true" />
          {empty ? (
            <span>데이터 없음</span>
          ) : (
            <span className="tabular-nums">{displayYear ? `${displayYear}년` : '연도'}</span>
          )}
          {!empty && !isExplicit && (
            <span className="hidden text-xs text-muted-foreground sm:inline">자동</span>
          )}
          <ChevronDown className="opacity-60" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          사업 조회 기준 연도
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={isExplicit && displayYear ? String(displayYear) : AUTO}
          onValueChange={(v) => select(v === AUTO ? null : Number(v))}
        >
          <DropdownMenuRadioItem value={AUTO}>
            최근 연도 자동{data?.defaultYear ? ` (${data.defaultYear}년)` : ''}
          </DropdownMenuRadioItem>
          {years.map((year) => (
            <DropdownMenuRadioItem key={year} value={String(year)} className="tabular-nums">
              {year}년
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
